import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addAttachmentMessage,
  addMessage,
  AddConversationMessageOptions,
  cancelScheduledMessage,
  generateAiDraft,
  generateAiSummary,
  getMentionSuggestions,
  getScheduledMessages,
  markManyAsRead,
  scheduleAttachmentMessage,
  scheduleMessage,
  ScheduledMessage,
  updateConversationTyping,
  updateScheduledMessageTime,
} from '../services/conversationService';
import { ConversationInteractiveButtonItem, ConversationInteractiveButtons, ConversationInteractiveList, ConversationInteractiveListRow, ConversationInteractiveListSection, ConversationMention, ConversationMentionInput, ConversationParticipantOption, ConversationParticipantRef, Message } from '../types/Conversation';
import { PendingAttachment, createPendingAttachment, revokePendingAttachments, selectPendingAttachments } from '../../../shared/utils/conversationAttachments';
import { getMicrophoneErrorKey, isMicrophoneSupported, requestMicrophoneStream } from '../../../shared/utils/microphone';
import { formatExternalChannelSendError } from '../utils/externalChannelSendError';
import { createOptimisticMessage } from '../utils/optimisticMessages';
import { findLatestInboundInstagramMessageId, findLatestInboundMessengerMessageId, findLatestInboundWhatsAppMessageId, conversationHasInstagramMessages, conversationHasMessengerMessages, conversationHasWhatsAppMessages, messageBodyContainsUrl } from '../utils/whatsappComposerUtils';
import { instagramCommentReplyBlocksAttachments, isInstagramCommentMessage, isInstagramCommentReplyTarget, messageToQuotedMessage, resolveInstagramCommentQuotedMessageId, resolveInstagramCommentTargetMessage, resolveInstagramComposerContext, type InstagramComposerContext, type InstagramReplyMode } from '../utils/instagramComposerUtils';
import { isMessengerCommentMessage, isMessengerCommentReplyTarget, messengerCommentReplyBlocksAttachments, resolveMessengerCommentQuotedMessageId, resolveMessengerCommentTargetMessage, resolveMessengerComposerContext, resolveConversationHasMessengerComments, type MessengerComposerContext, type MessengerReplyMode } from '../utils/messengerComposerUtils';
import type { AttachmentKind } from '../../../shared/utils/conversationAttachments';

const WHATSAPP_TYPING_COOLDOWN_MS = 20_000;
const WEBCHAT_TYPING_COOLDOWN_MS = 3_000;

const conversationHasWebchatMessages = (messages: Message[]): boolean =>
  messages.some(message => message.source === 'webchat');

type InteractiveComposerMode = 'list' | 'buttons';

const createInteractiveDraftId = (prefix: 'row' | 'btn'): string => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const getAttachmentMessageType = (kind: AttachmentKind): string => (kind === 'document' ? 'document' : kind);

const createEmptyInteractiveRow = (): ConversationInteractiveListRow => ({
  id: '',
  title: '',
  description: '',
});

const createEmptyInteractiveSection = (): ConversationInteractiveListSection => ({
  title: '',
  rows: [createEmptyInteractiveRow()],
});

const createEmptyInteractiveListDraft = (): ConversationInteractiveList => ({
  headerText: '',
  bodyText: '',
  footerText: '',
  buttonText: '',
  sections: [createEmptyInteractiveSection()],
});

const createEmptyInteractiveButton = (): ConversationInteractiveButtonItem => ({
  id: '',
  title: '',
});

const createEmptyInteractiveButtonsDraft = (): ConversationInteractiveButtons => ({
  headerText: '',
  bodyText: '',
  footerText: '',
  buttons: [createEmptyInteractiveButton()],
});

const normalizeInteractiveDraft = (interactiveList: ConversationInteractiveList): ConversationInteractiveList => ({
  headerText: interactiveList.headerText?.trim() || undefined,
  bodyText: interactiveList.bodyText.trim(),
  footerText: interactiveList.footerText?.trim() || undefined,
  buttonText: interactiveList.buttonText.trim(),
  sections: interactiveList.sections
    .map(section => ({
      title: section.title?.trim() || undefined,
      rows: section.rows
        .map(row => ({
          id: row.id.trim() || createInteractiveDraftId('row'),
          title: row.title.trim(),
          description: row.description?.trim() || undefined,
        }))
        .filter(row => row.title),
    }))
    .filter(section => section.rows.length > 0),
});

const normalizeInteractiveButtonsDraft = (interactiveButtons: ConversationInteractiveButtons): ConversationInteractiveButtons => ({
  headerText: interactiveButtons.headerText?.trim() || undefined,
  bodyText: interactiveButtons.bodyText.trim(),
  footerText: interactiveButtons.footerText?.trim() || undefined,
  buttons: interactiveButtons.buttons
    .map(button => ({
      id: button.id.trim() || createInteractiveDraftId('btn'),
      title: button.title.trim(),
    }))
    .filter(button => button.title),
});

const hasInteractiveComposerContent = (interactiveList: ConversationInteractiveList): boolean => {
  if (interactiveList.headerText?.trim() || interactiveList.bodyText.trim() || interactiveList.footerText?.trim() || interactiveList.buttonText.trim()) {
    return true;
  }

  return interactiveList.sections.some(section =>
    section.title?.trim() || section.rows.some(row => row.title.trim() || row.description?.trim())
  );
};

const hasInteractiveButtonsComposerContent = (interactiveButtons: ConversationInteractiveButtons): boolean => {
  if (interactiveButtons.headerText?.trim() || interactiveButtons.bodyText.trim() || interactiveButtons.footerText?.trim()) {
    return true;
  }

  return interactiveButtons.buttons.some(button => button.title.trim());
};

interface MentionTriggerState {
  start: number;
  end: number;
  query: string;
}

interface DraftMentionSelection {
  mentionedParticipantId: string;
  mentionedParticipantType: ConversationParticipantOption['type'];
  mentionedParticipantName: string;
  mentionText: string;
}

const findMentionTrigger = (text: string, caretPosition: number): MentionTriggerState | null => {
  if (caretPosition < 0 || caretPosition > text.length) {
    return null;
  }

  const prefix = text.slice(0, caretPosition);
  const atIndex = prefix.lastIndexOf('@');
  if (atIndex < 0) {
    return null;
  }

  if (atIndex > 0 && !/\s/.test(prefix[atIndex - 1])) {
    return null;
  }

  const rawQuery = prefix.slice(atIndex + 1);
  if (/\s/.test(rawQuery)) {
    return null;
  }

  return {
    start: atIndex,
    end: caretPosition,
    query: rawQuery,
  };
};

const buildMentionInputs = (content: string, mentions: DraftMentionSelection[]): ConversationMentionInput[] => {
  if (!content || mentions.length === 0) {
    return [];
  }

  let searchFrom = 0;
  const payload: ConversationMentionInput[] = [];

  for (const mention of mentions) {
    const position = content.indexOf(mention.mentionText, searchFrom);
    if (position < 0) {
      continue;
    }

    payload.push({
      mentionedParticipantId: mention.mentionedParticipantId,
      mentionedParticipantType: mention.mentionedParticipantType,
      mentionText: mention.mentionText,
      position,
      length: mention.mentionText.length,
    });

    searchFrom = position + mention.mentionText.length;
  }

  return payload;
};

const buildOptimisticMentions = (content: string, mentions: DraftMentionSelection[]): ConversationMention[] =>
  buildMentionInputs(content, mentions).map(input => {
    const selection = mentions.find(mention => mention.mentionedParticipantId === input.mentionedParticipantId);
    return {
      ...input,
      mentionedParticipantName: selection?.mentionedParticipantName ?? input.mentionText.replace(/^@/, ''),
    };
  });

interface ComposerRollbackState {
  composer: string;
  selectedMentions: DraftMentionSelection[];
  quotedMessage: Message | null;
  attachments: PendingAttachment[];
  interactiveComposerMode: InteractiveComposerMode;
  interactiveListDraft: ConversationInteractiveList;
  interactiveButtonsDraft: ConversationInteractiveButtons;
  isInteractiveComposerOpen: boolean;
}

interface UseMessageComposerOptions {
  conversationId?: string;
  meSender?: ConversationParticipantRef;
  containsWhatsAppMessages?: boolean;
  containsInstagramMessages?: boolean;
  containsInstagramComments?: boolean;
  containsMessengerMessages?: boolean;
  containsMessengerComments?: boolean;
  containsWebchatMessages?: boolean;
  conversationMessages?: Message[];
  instagramDefaultCommentReplyMode?: 'public_comment' | 'private_comment';
  messengerDefaultCommentReplyMode?: 'public_comment' | 'private_comment';
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  composerInputRef: React.RefObject<HTMLTextAreaElement | null>;
  resetKey?: string;
  onMessagesCommitted: (conversationId: string, sentMessages: Message[]) => void;
  onMessagesReconciled: (conversationId: string, replacements: Array<{ optimisticId: string; message: Message }>) => void;
  onMessagesRemoved: (conversationId: string, messageIds: string[]) => void;
  onAiDraftFailure?: (message: string) => void;
}

interface UseMessageComposerResult {
  composer: string;
  setComposer: React.Dispatch<React.SetStateAction<string>>;
  quotedMessage: Message | null;
  mentionSuggestions: ConversationParticipantOption[];
  mentionMenuOpen: boolean;
  mentionActiveIndex: number;
  interactiveComposerMode: InteractiveComposerMode;
  setInteractiveComposerMode: React.Dispatch<React.SetStateAction<InteractiveComposerMode>>;
  interactiveListDraft: ConversationInteractiveList;
  setInteractiveListDraft: React.Dispatch<React.SetStateAction<ConversationInteractiveList>>;
  interactiveButtonsDraft: ConversationInteractiveButtons;
  setInteractiveButtonsDraft: React.Dispatch<React.SetStateAction<ConversationInteractiveButtons>>;
  isInteractiveComposerOpen: boolean;
  setIsInteractiveComposerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hasInteractiveListDraft: boolean;
  hasInteractiveButtonsDraft: boolean;
  hasInteractiveDraft: boolean;
  attachments: PendingAttachment[];
  isUploaderExpanded: boolean;
  setIsUploaderExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isRecordingAudio: boolean;
  isShortcutMenuOpen: boolean;
  setIsShortcutMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dragActive: boolean;
  sendError: string | null;
  sendingMessage: boolean;
  sendingProgressLabel: string | null;
  aiDraft: string;
  aiDraftError: string | null;
  aiDraftLoading: boolean;
  aiSummary: string;
  aiSummaryError: string | null;
  aiSummaryLoading: boolean;
  scheduledAt: Date | null;
  setScheduledAt: React.Dispatch<React.SetStateAction<Date | null>>;
  scheduling: boolean;
  scheduleError: string | null;
  scheduledMessages: ScheduledMessage[];
  onSchedule: () => Promise<void>;
  onScheduleAt: (scheduledAt: Date) => Promise<void>;
  onRefreshScheduledMessages: () => Promise<void>;
  onCancelScheduledMessage: (scheduledMessageId: string) => Promise<void>;
  onUpdateScheduledMessageTime: (scheduledMessageId: string, scheduledAt: Date, content?: string) => Promise<void>;
  onSend: () => Promise<void>;
  onGenerateAiDraft: () => Promise<void>;
  onGenerateAiSummary: () => Promise<void>;
  onAudioRecordToggle: () => Promise<void>;
  onShortcutSelectCommand: (command: string) => void;
  insertComposerEmoji: (emoji: string) => void;
  clearAttachments: () => void;
  removeAttachment: (attachmentId: string) => void;
  onPickAttachments: (fileList?: FileList | File[] | null) => void;
  onComposerDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onComposerDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onComposerDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onComposerChange: (value: string, caretPosition: number) => void;
  onComposerSelectionChange: (caretPosition: number) => void;
  onComposerFocus: () => void;
  onComposerKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
  onSelectMention: (option: ConversationParticipantOption) => void;
  onQuoteMessage: (message: Message) => void;
  onSelectInstagramCommentTarget: (message: Message) => void;
  onClearInstagramCommentTarget: () => void;
  onSelectMessengerCommentTarget: (message: Message) => void;
  onClearMessengerCommentTarget: () => void;
  onReplyWithInteractiveSelection: (message: Message, replyText: string) => Promise<void>;
  onClearQuotedMessage: () => void;
  onUseAiDraft: () => void;
  onUseAiSummary: () => void;
  onDismissAiDraft: () => void;
  onDismissAiSummary: () => void;
  showWhatsAppLinkPreviewToggle: boolean;
  whatsAppLinkPreviewEnabled: boolean;
  setWhatsAppLinkPreviewEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  instagramComposerContext: InstagramComposerContext;
  instagramReplyMode: InstagramReplyMode | null;
  setInstagramReplyMode: React.Dispatch<React.SetStateAction<InstagramReplyMode | null>>;
  instagramCommentTargetMessageId: string | null;
  setInstagramCommentTargetMessageId: React.Dispatch<React.SetStateAction<string | null>>;
  messengerComposerContext: MessengerComposerContext;
  messengerReplyMode: MessengerReplyMode | null;
  setMessengerReplyMode: React.Dispatch<React.SetStateAction<MessengerReplyMode | null>>;
  messengerCommentTargetMessageId: string | null;
  setMessengerCommentTargetMessageId: React.Dispatch<React.SetStateAction<string | null>>;
}

export const useMessageComposer = ({
  conversationId,
  meSender,
  containsWhatsAppMessages = false,
  containsInstagramMessages = false,
  containsInstagramComments = false,
  containsMessengerMessages = false,
  containsMessengerComments = false,
  containsWebchatMessages = false,
  conversationMessages = [],
  instagramDefaultCommentReplyMode = 'public_comment',
  messengerDefaultCommentReplyMode = 'public_comment',
  fileInputRef,
  composerInputRef,
  resetKey,
  onMessagesCommitted,
  onMessagesReconciled,
  onMessagesRemoved,
  onAiDraftFailure,
}: UseMessageComposerOptions): UseMessageComposerResult => {
  const { t } = useTranslation('conversations');
  const [composer, setComposer] = useState('');
  const [quotedMessage, setQuotedMessage] = useState<Message | null>(null);
  const [interactiveComposerMode, setInteractiveComposerMode] = useState<InteractiveComposerMode>('list');
  const [interactiveListDraft, setInteractiveListDraft] = useState<ConversationInteractiveList>(createEmptyInteractiveListDraft);
  const [interactiveButtonsDraft, setInteractiveButtonsDraft] = useState<ConversationInteractiveButtons>(createEmptyInteractiveButtonsDraft);
  const [isInteractiveComposerOpen, setIsInteractiveComposerOpen] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [isUploaderExpanded, setIsUploaderExpanded] = useState(false);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [isShortcutMenuOpen, setIsShortcutMenuOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendingProgressLabel, setSendingProgressLabel] = useState<string | null>(null);
  const [aiDraft, setAiDraft] = useState('');
  const [aiDraftError, setAiDraftError] = useState<string | null>(null);
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiSummaryError, setAiSummaryError] = useState<string | null>(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [mentionSuggestions, setMentionSuggestions] = useState<ConversationParticipantOption[]>([]);
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [mentionTrigger, setMentionTrigger] = useState<MentionTriggerState | null>(null);
  const [, setComposerCaretPosition] = useState(0);
  const composerCaretPositionRef = useRef(0);

  const updateComposerCaretPosition = useCallback((caretPosition: number) => {
    composerCaretPositionRef.current = caretPosition;
    setComposerCaretPosition(caretPosition);
  }, []);
  const [selectedMentions, setSelectedMentions] = useState<DraftMentionSelection[]>([]);
  const [whatsAppLinkPreviewEnabled, setWhatsAppLinkPreviewEnabled] = useState(true);

  const attachmentRef = useRef<PendingAttachment[]>([]);
  const lastWhatsAppTypingSignalAtRef = useRef(0);
  const lastInstagramTypingSignalAtRef = useRef(0);
  const lastMessengerTypingSignalAtRef = useRef(0);
  const lastWebchatTypingSignalAtRef = useRef(0);

  const resolvedContainsWhatsAppMessages = containsWhatsAppMessages || conversationHasWhatsAppMessages(conversationMessages);
  const resolvedContainsInstagramMessages = containsInstagramMessages || conversationHasInstagramMessages(conversationMessages);
  const resolvedContainsInstagramComments = containsInstagramComments
    || conversationMessages.some(message => message.channelContext?.commentId && message.source === 'instagram');
  const resolvedContainsMessengerMessages = containsMessengerMessages || conversationHasMessengerMessages(conversationMessages);
  const resolvedContainsMessengerComments = containsMessengerComments
    || resolveConversationHasMessengerComments({ containsMessengerComments, messages: conversationMessages });
  const resolvedContainsWebchatMessages = containsWebchatMessages || conversationHasWebchatMessages(conversationMessages);

  const resolveSendErrorMessage = useCallback((error: unknown, fallback: string) => {
    return formatExternalChannelSendError(error instanceof Error ? error.message : null, fallback);
  }, []);

  const showWhatsAppLinkPreviewToggle = resolvedContainsWhatsAppMessages
    && !isInteractiveComposerOpen
    && messageBodyContainsUrl(composer);

  const buildWhatsAppPreviewOptions = useCallback((content: string): Pick<AddConversationMessageOptions, 'previewUrl'> => {
    if (!resolvedContainsWhatsAppMessages || !messageBodyContainsUrl(content)) {
      return {};
    }

    if (!whatsAppLinkPreviewEnabled) {
      return { previewUrl: false };
    }

    return { previewUrl: true };
  }, [resolvedContainsWhatsAppMessages, whatsAppLinkPreviewEnabled]);

  const signalWhatsAppTyping = useCallback(async () => {
    if (!conversationId || !meSender || !resolvedContainsWhatsAppMessages) {
      return;
    }

    const inboundMessageId = findLatestInboundWhatsAppMessageId(conversationMessages);
    if (!inboundMessageId) {
      return;
    }

    const now = Date.now();
    if (now - lastWhatsAppTypingSignalAtRef.current < WHATSAPP_TYPING_COOLDOWN_MS) {
      return;
    }

    lastWhatsAppTypingSignalAtRef.current = now;

    try {
      await markManyAsRead(conversationId, [inboundMessageId], meSender, { showWhatsAppTypingIndicator: true });
    } catch {
      lastWhatsAppTypingSignalAtRef.current = 0;
    }
  }, [resolvedContainsWhatsAppMessages, conversationId, conversationMessages, meSender]);

  const signalInstagramTyping = useCallback(async () => {
    if (!conversationId || !meSender || !resolvedContainsInstagramMessages) {
      return;
    }

    const inboundMessageId = findLatestInboundInstagramMessageId(conversationMessages);
    if (!inboundMessageId) {
      return;
    }

    const now = Date.now();
    if (now - lastInstagramTypingSignalAtRef.current < WHATSAPP_TYPING_COOLDOWN_MS) {
      return;
    }

    lastInstagramTypingSignalAtRef.current = now;

    try {
      await markManyAsRead(conversationId, [inboundMessageId], meSender, { showInstagramTypingIndicator: true });
    } catch {
      lastInstagramTypingSignalAtRef.current = 0;
    }
  }, [resolvedContainsInstagramMessages, conversationId, conversationMessages, meSender]);

  const signalMessengerTyping = useCallback(async () => {
    if (!conversationId || !meSender || !resolvedContainsMessengerMessages) {
      return;
    }

    const inboundMessageId = findLatestInboundMessengerMessageId(conversationMessages);
    if (!inboundMessageId) {
      return;
    }

    const now = Date.now();
    if (now - lastMessengerTypingSignalAtRef.current < WHATSAPP_TYPING_COOLDOWN_MS) {
      return;
    }

    lastMessengerTypingSignalAtRef.current = now;

    try {
      await markManyAsRead(conversationId, [inboundMessageId], meSender, { showMessengerTypingIndicator: true });
    } catch {
      lastMessengerTypingSignalAtRef.current = 0;
    }
  }, [resolvedContainsMessengerMessages, conversationId, conversationMessages, meSender]);

  const stopWebchatTyping = useCallback(async () => {
    if (!conversationId || !meSender || !resolvedContainsWebchatMessages) {
      return;
    }

    try {
      await updateConversationTyping(conversationId, meSender, false);
    } catch {
      // Ignore typing cleanup failures.
    }
  }, [conversationId, meSender, resolvedContainsWebchatMessages]);

  const signalWebchatTyping = useCallback(async () => {
    if (!conversationId || !meSender || !resolvedContainsWebchatMessages) {
      return;
    }

    const now = Date.now();
    if (now - lastWebchatTypingSignalAtRef.current < WEBCHAT_TYPING_COOLDOWN_MS) {
      return;
    }

    lastWebchatTypingSignalAtRef.current = now;

    try {
      await updateConversationTyping(conversationId, meSender, true);
    } catch {
      lastWebchatTypingSignalAtRef.current = 0;
    }
  }, [conversationId, meSender, resolvedContainsWebchatMessages]);

  const signalChannelTyping = useCallback(async () => {
    await Promise.all([
      signalWhatsAppTyping(),
      signalInstagramTyping(),
      signalMessengerTyping(),
      signalWebchatTyping(),
    ]);
  }, [signalInstagramTyping, signalMessengerTyping, signalWebchatTyping, signalWhatsAppTyping]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [fileInputRef]);

  const removeAttachment = useCallback((attachmentId: string) => {
    setAttachments(current => current.filter(attachment => attachment.id !== attachmentId));
  }, []);

  const clearInteractiveComposer = useCallback(() => {
    setInteractiveComposerMode('list');
    setInteractiveListDraft(createEmptyInteractiveListDraft());
    setInteractiveButtonsDraft(createEmptyInteractiveButtonsDraft());
    setIsInteractiveComposerOpen(false);
  }, []);

  const hasInteractiveListDraft = hasInteractiveComposerContent(interactiveListDraft);
  const hasInteractiveButtonsDraft = hasInteractiveButtonsComposerContent(interactiveButtonsDraft);
  const hasInteractiveDraft = hasInteractiveListDraft || hasInteractiveButtonsDraft;
  const isInteractiveModeActive = isInteractiveComposerOpen;
  const [instagramReplyModeOverride, setInstagramReplyModeOverride] = useState<InstagramReplyMode | null>(null);
  const [instagramCommentTargetMessageId, setInstagramCommentTargetMessageId] = useState<string | null>(null);
  const [messengerReplyModeOverride, setMessengerReplyModeOverride] = useState<MessengerReplyMode | null>(null);
  const [messengerCommentTargetMessageId, setMessengerCommentTargetMessageId] = useState<string | null>(null);

  const instagramComposerContext = useMemo(
    () => resolveInstagramComposerContext(
      conversationMessages,
      quotedMessage,
      instagramDefaultCommentReplyMode,
      resolvedContainsInstagramComments,
      instagramCommentTargetMessageId,
    ),
    [conversationMessages, quotedMessage, instagramDefaultCommentReplyMode, resolvedContainsInstagramComments, instagramCommentTargetMessageId],
  );

  const instagramCommentTargetMessage = useMemo(
    () => resolveInstagramCommentTargetMessage(conversationMessages, {
      quotedMessage,
      selectedCommentMessageId: instagramCommentTargetMessageId,
    }),
    [conversationMessages, quotedMessage, instagramCommentTargetMessageId],
  );

  const messengerComposerContext = useMemo(
    () => resolveMessengerComposerContext(
      conversationMessages,
      quotedMessage,
      messengerDefaultCommentReplyMode,
      resolvedContainsMessengerComments,
      messengerCommentTargetMessageId,
    ),
    [conversationMessages, quotedMessage, messengerDefaultCommentReplyMode, resolvedContainsMessengerComments, messengerCommentTargetMessageId],
  );

  const messengerCommentTargetMessage = useMemo(
    () => resolveMessengerCommentTargetMessage(conversationMessages, {
      quotedMessage,
      selectedCommentMessageId: messengerCommentTargetMessageId,
    }),
    [conversationMessages, quotedMessage, messengerCommentTargetMessageId],
  );

  const instagramReplyMode = instagramReplyModeOverride ?? instagramComposerContext.replyMode;
  const messengerReplyMode = messengerReplyModeOverride ?? messengerComposerContext.replyMode;

  const quotedMessageIdForSend = useMemo(() => {
    if (instagramCommentTargetMessageId && instagramCommentTargetMessage) {
      return resolveInstagramCommentQuotedMessageId(
        instagramReplyMode,
        instagramCommentTargetMessage,
        quotedMessage,
      );
    }

    if (messengerCommentTargetMessageId && messengerCommentTargetMessage) {
      return resolveMessengerCommentQuotedMessageId(
        messengerReplyMode,
        messengerCommentTargetMessage,
        quotedMessage,
      );
    }

    return quotedMessage?.id;
  }, [
    instagramCommentTargetMessage,
    instagramCommentTargetMessageId,
    instagramReplyMode,
    messengerCommentTargetMessage,
    messengerCommentTargetMessageId,
    messengerReplyMode,
    quotedMessage,
  ]);

  useEffect(() => {
    setInstagramReplyModeOverride(null);
    setInstagramCommentTargetMessageId(null);
    setMessengerReplyModeOverride(null);
    setMessengerCommentTargetMessageId(null);
  }, [conversationId, resetKey]);

  useEffect(() => {
    setInstagramReplyModeOverride(null);
  }, [instagramCommentTargetMessageId]);

  useEffect(() => {
    setMessengerReplyModeOverride(null);
  }, [messengerCommentTargetMessageId]);

  const commentAttachmentsBlocked = instagramCommentReplyBlocksAttachments(instagramReplyMode)
    || messengerCommentReplyBlocksAttachments(messengerReplyMode);

  const buildReplyOptions = useCallback((): Pick<AddConversationMessageOptions, 'instagramReplyMode' | 'messengerReplyMode'> => {
    const options: Pick<AddConversationMessageOptions, 'instagramReplyMode' | 'messengerReplyMode'> = {};

    if (instagramCommentTargetMessageId && instagramReplyMode) {
      options.instagramReplyMode = instagramReplyMode;
    }

    if (messengerCommentTargetMessageId && messengerReplyMode) {
      options.messengerReplyMode = messengerReplyMode;
    }

    return options;
  }, [instagramCommentTargetMessageId, instagramReplyMode, messengerCommentTargetMessageId, messengerReplyMode]);

  const onPickAttachments = useCallback((fileList?: FileList | File[] | null) => {
    if (!fileList?.length) return;

    if (commentAttachmentsBlocked) {
      setSendError(t('instagramPrivateReplyAttachmentsBlocked'));
      return;
    }

    if (isInteractiveModeActive) {
      setSendError(t('interactiveCannotIncludeAttachments'));
      return;
    }

    const selection = selectPendingAttachments(Array.from(fileList));
    if (selection.attachments.length > 0) {
      setAttachments(current => [...current, ...selection.attachments]);
      setIsUploaderExpanded(true);
    }

    setSendError(selection.errors.length > 0 ? selection.errors.join('\n') : null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [fileInputRef, commentAttachmentsBlocked, isInteractiveModeActive, t]);

  const stopRecordingStream = useCallback(() => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach(track => track.stop());
      recordingStreamRef.current = null;
    }
  }, []);

  const stopAudioRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    if (recorder.state !== 'inactive') {
      recorder.stop();
    }
  }, []);

  useEffect(() => {
    if (!commentAttachmentsBlocked) {
      return;
    }

    setAttachments(current => {
      current.forEach(attachment => {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
      return [];
    });
    setIsUploaderExpanded(false);
    if (isRecordingAudio) {
      stopAudioRecording();
    }
    if (isInteractiveModeActive) {
      setIsInteractiveComposerOpen(false);
    }
  }, [
    commentAttachmentsBlocked,
    isInteractiveModeActive,
    isRecordingAudio,
    stopAudioRecording,
  ]);

  const startAudioRecording = useCallback(async () => {
    if (!isMicrophoneSupported() || typeof MediaRecorder === 'undefined') {
      setSendError(t('microphoneUnsupported', { ns: 'common' }));
      return;
    }

    try {
      const stream = await requestMicrophoneStream();
      const preferredMimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
      const mimeType = preferredMimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

      recordingStreamRef.current = stream;
      recordingChunksRef.current = [];

      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const recordingMimeType = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0].trim() || 'audio/webm';
        const extension = recordingMimeType.includes('ogg') ? 'ogg' : 'webm';
        const recordedBlob = new Blob(recordingChunksRef.current, { type: recordingMimeType });

        if (recordedBlob.size > 0) {
          const recordedFile = new File([recordedBlob], `voice-note.${extension}`, { type: recordingMimeType });
          const pendingAttachment = createPendingAttachment(recordedFile);
          setAttachments(current => [...current, pendingAttachment]);
          setIsUploaderExpanded(true);
          setSendError(null);
        }

        recordingChunksRef.current = [];
        mediaRecorderRef.current = null;
        stopRecordingStream();
        setIsRecordingAudio(false);
      };

      recorder.onerror = () => {
        setSendError(t('audioRecordingFailed'));
        mediaRecorderRef.current = null;
        recordingChunksRef.current = [];
        stopRecordingStream();
        setIsRecordingAudio(false);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecordingAudio(true);
      setSendError(null);
    } catch (recordingError) {
      setSendError(t(getMicrophoneErrorKey(recordingError), { ns: 'common' }));
      stopRecordingStream();
      setIsRecordingAudio(false);
    }
  }, [stopRecordingStream, t]);

  const onAudioRecordToggle = useCallback(async () => {
    if (commentAttachmentsBlocked) {
      setSendError(t('instagramPrivateReplyAttachmentsBlocked'));
      return;
    }

    if (isInteractiveModeActive) {
      setSendError(t('interactiveListCannotIncludeAttachments'));
      return;
    }

    if (isRecordingAudio) {
      stopAudioRecording();
      return;
    }

    await startAudioRecording();
  }, [commentAttachmentsBlocked, isInteractiveModeActive, isRecordingAudio, startAudioRecording, stopAudioRecording, t]);

  const onShortcutSelectCommand = useCallback((command: string) => {
    setComposer(command);
    setTimeout(() => {
      const input = composerInputRef.current;
      if (!input) return;

      input.focus();
      const cursorPosition = input.value.length;
      input.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  }, [composerInputRef]);

  const focusComposerAtEnd = useCallback((value?: string) => {
    setTimeout(() => {
      const input = composerInputRef.current;
      if (!input) {
        return;
      }

      input.focus();
      const cursorPosition = typeof value === 'string' ? value.length : input.value.length;
      input.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  }, [composerInputRef]);

  const onComposerChange = useCallback((value: string, caretPosition: number) => {
    setComposer(value);
    updateComposerCaretPosition(caretPosition);
    setMentionTrigger(findMentionTrigger(value, caretPosition));
    if (value.trim()) {
      void signalChannelTyping();
    }
  }, [signalChannelTyping, updateComposerCaretPosition]);

  const onComposerFocus = useCallback(() => {
    if (composer.trim()) {
      void signalChannelTyping();
    }
  }, [composer, signalChannelTyping]);

  const onComposerSelectionChange = useCallback((caretPosition: number) => {
    updateComposerCaretPosition(caretPosition);
    setMentionTrigger(current => findMentionTrigger(composer, caretPosition) ?? (current && current.end === caretPosition ? current : null));
  }, [composer, updateComposerCaretPosition]);

  const onSelectMention = useCallback((option: ConversationParticipantOption) => {
    if (!mentionTrigger) {
      return;
    }

    const mentionText = `@${option.name}`;
    const before = composer.slice(0, mentionTrigger.start);
    const after = composer.slice(mentionTrigger.end);
    const nextComposer = `${before}${mentionText} ${after}`;
    const nextCaretPosition = before.length + mentionText.length + 1;

    setComposer(nextComposer);
    setSelectedMentions(current => ([
      ...current,
      {
        mentionedParticipantId: option.id,
        mentionedParticipantType: option.type,
        mentionedParticipantName: option.name,
        mentionText,
      },
    ]));
    setMentionMenuOpen(false);
    setMentionSuggestions([]);
    setMentionActiveIndex(0);
    setMentionTrigger(null);
    updateComposerCaretPosition(nextCaretPosition);

    setTimeout(() => {
      const input = composerInputRef.current;
      if (!input) {
        return;
      }

      input.focus();
      input.setSelectionRange(nextCaretPosition, nextCaretPosition);
    }, 0);
  }, [composer, composerInputRef, mentionTrigger, updateComposerCaretPosition]);

  const insertComposerEmoji = useCallback((emoji: string) => {
    const input = composerInputRef.current;
    const inputFocused = Boolean(input && document.activeElement === input);
    const selectionStart = input?.selectionStart;
    const selectionEnd = input?.selectionEnd;
    const start = inputFocused && typeof selectionStart === 'number' ? selectionStart : composerCaretPositionRef.current;
    const end = inputFocused && typeof selectionEnd === 'number' ? selectionEnd : start;
    const before = composer.slice(0, start);
    const after = composer.slice(end);
    const nextComposer = `${before}${emoji}${after}`;
    const nextCaretPosition = before.length + emoji.length;

    onComposerChange(nextComposer, nextCaretPosition);

    setTimeout(() => {
      const nextInput = composerInputRef.current;
      if (!nextInput) {
        return;
      }

      nextInput.focus();
      nextInput.setSelectionRange(nextCaretPosition, nextCaretPosition);
    }, 0);
  }, [composer, composerInputRef, onComposerChange]);

  const onQuoteMessage = useCallback((message: Message) => {
    if (isInstagramCommentReplyTarget(message)) {
      setInstagramCommentTargetMessageId(message.id);
      setMessengerCommentTargetMessageId(null);
      setQuotedMessage(null);
    } else if (isMessengerCommentReplyTarget(message)) {
      setMessengerCommentTargetMessageId(message.id);
      setInstagramCommentTargetMessageId(null);
      setQuotedMessage(null);
    } else {
      setQuotedMessage(message);
    }
    focusComposerAtEnd();
  }, [focusComposerAtEnd]);

  const onSelectInstagramCommentTarget = useCallback((message: Message) => {
    if (!isInstagramCommentMessage(message)) {
      return;
    }

    setInstagramCommentTargetMessageId(message.id);
    setMessengerCommentTargetMessageId(null);
    setQuotedMessage(null);
    focusComposerAtEnd();
  }, [focusComposerAtEnd]);

  const onClearInstagramCommentTarget = useCallback(() => {
    setInstagramCommentTargetMessageId(null);
  }, []);

  const onSelectMessengerCommentTarget = useCallback((message: Message) => {
    if (!isMessengerCommentMessage(message)) {
      return;
    }

    setMessengerCommentTargetMessageId(message.id);
    setInstagramCommentTargetMessageId(null);
    setQuotedMessage(null);
    focusComposerAtEnd();
  }, [focusComposerAtEnd]);

  const onClearMessengerCommentTarget = useCallback(() => {
    setMessengerCommentTargetMessageId(null);
  }, []);

  const onReplyWithInteractiveSelection = useCallback(async (message: Message, replyText: string) => {
    if (!conversationId || !meSender) {
      return;
    }

    const trimmedReplyText = replyText.trim();
    if (!trimmedReplyText) {
      return;
    }

    if (composer.trim() || attachments.length > 0 || isInteractiveModeActive) {
      setQuotedMessage(message);
      setComposer(trimmedReplyText);
      setSelectedMentions([]);
      setSendError(null);
      focusComposerAtEnd(trimmedReplyText);
      return;
    }

    setSendError(null);
    const rollback: ComposerRollbackState = {
      composer,
      selectedMentions: [...selectedMentions],
      quotedMessage: message,
      attachments: [...attachments],
      interactiveComposerMode,
      interactiveListDraft,
      interactiveButtonsDraft,
      isInteractiveComposerOpen,
    };

    const optimisticMessage = createOptimisticMessage({
      conversationId,
      sender: meSender,
      content: trimmedReplyText,
      quotedMessageId: message.id,
    });

    setComposer('');
    setSelectedMentions([]);
    setQuotedMessage(null);
    onMessagesCommitted(conversationId, [optimisticMessage]);

    try {
      const sentMessage = await addMessage(conversationId, meSender, trimmedReplyText, {
        quotedMessageId: message.id,
        ...buildReplyOptions(),
      } satisfies AddConversationMessageOptions);

      if (sentMessage) {
        onMessagesReconciled(conversationId, [{ optimisticId: optimisticMessage.id, message: sentMessage }]);
      } else {
        setComposer(rollback.composer);
        setSelectedMentions(rollback.selectedMentions);
        setQuotedMessage(rollback.quotedMessage);
        onMessagesRemoved(conversationId, [optimisticMessage.id]);
        setSendError(t('failedToSendMessage'));
      }
    } catch (error) {
      setComposer(rollback.composer);
      setSelectedMentions(rollback.selectedMentions);
      setQuotedMessage(rollback.quotedMessage);
      onMessagesRemoved(conversationId, [optimisticMessage.id]);
      setSendError(resolveSendErrorMessage(error, t('failedToSendMessage')));
      focusComposerAtEnd(trimmedReplyText);
    }
  }, [
    attachments,
    composer,
    conversationId,
    focusComposerAtEnd,
    interactiveButtonsDraft,
    interactiveComposerMode,
    interactiveListDraft,
    isInteractiveComposerOpen,
    isInteractiveModeActive,
    meSender,
    buildReplyOptions,
    onMessagesCommitted,
    onMessagesReconciled,
    onMessagesRemoved,
    resolveSendErrorMessage,
    selectedMentions,
    t,
  ]);

  const onClearQuotedMessage = useCallback(() => {
    setQuotedMessage(null);
  }, []);

  const onComposerKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!mentionMenuOpen || mentionSuggestions.length === 0) {
      return false;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionActiveIndex(current => (current + 1) % mentionSuggestions.length);
      return true;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionActiveIndex(current => (current - 1 + mentionSuggestions.length) % mentionSuggestions.length);
      return true;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      onSelectMention(mentionSuggestions[mentionActiveIndex]);
      return true;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setMentionMenuOpen(false);
      return true;
    }

    return false;
  }, [mentionActiveIndex, mentionMenuOpen, mentionSuggestions, onSelectMention]);

  useEffect(() => {
    if (!mentionTrigger) {
      setMentionMenuOpen(false);
      setMentionSuggestions([]);
      setMentionActiveIndex(0);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        const options = await getMentionSuggestions(mentionTrigger.query);
        if (cancelled) {
          return;
        }

        setMentionSuggestions(options);
        setMentionMenuOpen(options.length > 0);
        setMentionActiveIndex(0);
      } catch {
        if (cancelled) {
          return;
        }

        setMentionSuggestions([]);
        setMentionMenuOpen(false);
      }
    }, 120);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [mentionTrigger]);

  const onSend = useCallback(async () => {
    if (!conversationId || !meSender) return;

    void stopWebchatTyping();

    const trimmedComposer = composer.trim();
    if (!trimmedComposer && attachments.length === 0 && !(isInteractiveModeActive && hasInteractiveDraft)) return;

    setSendError(null);
    const queuedAttachments = [...attachments];
    const buildUploadProgressLabel = (kind: PendingAttachment['kind'], index: number, total: number) => {
      if (kind === 'video') {
        return t('uploadingVideo', { current: index, total });
      }

      if (kind === 'image') {
        return t('uploadingImage', { current: index, total });
      }

      if (kind === 'audio') {
        return t('uploadingAudio', { current: index, total });
      }

      return t('uploadingAttachment', { current: index, total });
    };

    const isAttachmentSend = queuedAttachments.length > 0;
    if (isAttachmentSend) {
      setSendingMessage(true);
      setSendingProgressLabel(buildUploadProgressLabel(queuedAttachments[0].kind, 1, queuedAttachments.length));
    }

    try {
    const mentionInputs = buildMentionInputs(composer, selectedMentions);
    const optimisticMentions = buildOptimisticMentions(composer, selectedMentions);

    const captureRollbackState = (): ComposerRollbackState => ({
      composer,
      selectedMentions: [...selectedMentions],
      quotedMessage,
      attachments: [...attachments],
      interactiveComposerMode,
      interactiveListDraft,
      interactiveButtonsDraft,
      isInteractiveComposerOpen,
    });

    const restoreRollbackState = (rollback: ComposerRollbackState) => {
      setComposer(rollback.composer);
      setSelectedMentions(rollback.selectedMentions);
      setQuotedMessage(rollback.quotedMessage);
      setAttachments(rollback.attachments);
      setInteractiveComposerMode(rollback.interactiveComposerMode);
      setInteractiveListDraft(rollback.interactiveListDraft);
      setInteractiveButtonsDraft(rollback.interactiveButtonsDraft);
      setIsInteractiveComposerOpen(rollback.isInteractiveComposerOpen);
    };

    const clearComposerAfterOptimisticSend = () => {
      setComposer('');
      setSelectedMentions([]);
      setAiDraft('');
      setAiDraftError(null);
      setQuotedMessage(null);
      clearInteractiveComposer();
      clearAttachments();
    };

    const resolveOptimisticQuotedMessage = () => {
      if (instagramCommentTargetMessage && quotedMessageIdForSend === instagramCommentTargetMessage.id) {
        return messageToQuotedMessage(instagramCommentTargetMessage);
      }

      if (messengerCommentTargetMessage && quotedMessageIdForSend === messengerCommentTargetMessage.id) {
        return messageToQuotedMessage(messengerCommentTargetMessage);
      }

      if (quotedMessage) {
        return messageToQuotedMessage(quotedMessage);
      }

      return undefined;
    };
    const optimisticQuotedMessage = resolveOptimisticQuotedMessage();

    const rollbackOnFailure = (rollback: ComposerRollbackState, optimisticIds: string[], errorMessage: string) => {
      restoreRollbackState(rollback);
      onMessagesRemoved(conversationId, optimisticIds);
      setSendError(errorMessage);
    };

    if (isInteractiveModeActive) {
      if (attachments.length > 0) {
        setSendError(t('interactiveCannotIncludeAttachments'));
        return;
      }

      if (interactiveComposerMode === 'buttons') {
        const normalizedInteractiveButtons = normalizeInteractiveButtonsDraft(interactiveButtonsDraft);
        if (!normalizedInteractiveButtons.bodyText) {
          setSendError(t('interactiveButtonsBodyRequired'));
          return;
        }

        if (normalizedInteractiveButtons.buttons.length === 0) {
          setSendError(t('interactiveButtonsRequired'));
          return;
        }

        const rollback = captureRollbackState();
        const optimisticMessage = createOptimisticMessage({
          conversationId,
          sender: meSender,
          content: normalizedInteractiveButtons.bodyText,
          messageType: 'interactive',
          quotedMessageId: quotedMessageIdForSend,
          quotedMessage: optimisticQuotedMessage,
          interactiveButtons: normalizedInteractiveButtons,
          mentions: optimisticMentions,
        });

        clearComposerAfterOptimisticSend();
        onMessagesCommitted(conversationId, [optimisticMessage]);

        try {
          const msg = await addMessage(conversationId, meSender, normalizedInteractiveButtons.bodyText, {
            quotedMessageId: quotedMessageIdForSend,
            messageType: 'interactive',
            interactiveButtons: normalizedInteractiveButtons,
            mentions: mentionInputs,
          } satisfies AddConversationMessageOptions);

          if (msg) {
            onMessagesReconciled(conversationId, [{
              optimisticId: optimisticMessage.id,
              message: { ...msg, interactiveButtons: normalizedInteractiveButtons },
            }]);
          } else {
            rollbackOnFailure(rollback, [optimisticMessage.id], t('failedToSendMessage'));
          }
        } catch (error) {
          rollbackOnFailure(
            rollback,
            [optimisticMessage.id],
            resolveSendErrorMessage(error, t('failedToSendMessage')),
          );
        }

        return;
      }

      const normalizedInteractiveList = normalizeInteractiveDraft(interactiveListDraft);
      if (!normalizedInteractiveList.bodyText) {
        setSendError(t('interactiveListBodyRequired'));
        return;
      }

      if (!normalizedInteractiveList.buttonText) {
        setSendError(t('interactiveListButtonRequired'));
        return;
      }

      if (normalizedInteractiveList.sections.length === 0) {
        setSendError(t('interactiveListSectionRequired'));
        return;
      }

      const hasEmptyRow = normalizedInteractiveList.sections.some(section => section.rows.length === 0);
      if (hasEmptyRow) {
        setSendError(t('interactiveListRowRequired'));
        return;
      }

      const rollback = captureRollbackState();
      const optimisticMessage = createOptimisticMessage({
        conversationId,
        sender: meSender,
        content: normalizedInteractiveList.bodyText,
        messageType: 'interactive',
        quotedMessageId: quotedMessageIdForSend,
        quotedMessage: optimisticQuotedMessage,
        interactiveList: normalizedInteractiveList,
        mentions: optimisticMentions,
      });

      clearComposerAfterOptimisticSend();
      onMessagesCommitted(conversationId, [optimisticMessage]);

      try {
        const msg = await addMessage(conversationId, meSender, normalizedInteractiveList.bodyText, {
          quotedMessageId: quotedMessageIdForSend,
          messageType: 'interactive',
          interactiveList: normalizedInteractiveList,
          mentions: mentionInputs,
        } satisfies AddConversationMessageOptions);

        if (msg) {
          onMessagesReconciled(conversationId, [{
            optimisticId: optimisticMessage.id,
            message: { ...msg, interactiveList: normalizedInteractiveList },
          }]);
        } else {
          rollbackOnFailure(rollback, [optimisticMessage.id], t('failedToSendMessage'));
        }
      } catch (error) {
        rollbackOnFailure(
          rollback,
          [optimisticMessage.id],
          resolveSendErrorMessage(error, t('failedToSendMessage')),
        );
      }

      return;
    }

    if (attachments.length === 0) {
      const rollback = captureRollbackState();
      const optimisticMessage = createOptimisticMessage({
        conversationId,
        sender: meSender,
        content: trimmedComposer,
        quotedMessageId: quotedMessageIdForSend,
        quotedMessage: optimisticQuotedMessage,
        mentions: optimisticMentions,
      });

      clearComposerAfterOptimisticSend();
      onMessagesCommitted(conversationId, [optimisticMessage]);

      try {
        const msg = await addMessage(conversationId, meSender, trimmedComposer, {
          quotedMessageId: quotedMessageIdForSend,
          mentions: mentionInputs,
          ...buildWhatsAppPreviewOptions(trimmedComposer),
          ...buildReplyOptions(),
        } satisfies AddConversationMessageOptions);

        if (msg) {
          onMessagesReconciled(conversationId, [{ optimisticId: optimisticMessage.id, message: msg }]);
        } else {
          rollbackOnFailure(rollback, [optimisticMessage.id], t('failedToSendMessage'));
        }
      } catch (error) {
        rollbackOnFailure(
          rollback,
          [optimisticMessage.id],
          resolveSendErrorMessage(error, t('failedToSendMessage')),
        );
      }

      return;
    }

    const rollback = captureRollbackState();
    const optimisticMessages = queuedAttachments.map((attachment, index) => createOptimisticMessage({
      conversationId,
      sender: meSender,
      content: index === 0 ? trimmedComposer : '',
      messageType: getAttachmentMessageType(attachment.kind),
      quotedMessageId: index === 0 ? quotedMessageIdForSend : undefined,
      quotedMessage: index === 0 ? optimisticQuotedMessage : undefined,
      attachment,
    }));

    clearComposerAfterOptimisticSend();
    onMessagesCommitted(conversationId, optimisticMessages);

    let failedIndex: number | null = null;
    let failureMessage = t('failedToSendAttachment');

    for (let index = 0; index < queuedAttachments.length; index += 1) {
      setSendingProgressLabel(buildUploadProgressLabel(queuedAttachments[index].kind, index + 1, queuedAttachments.length));
      try {
        const sentMessage = await addAttachmentMessage(
          conversationId,
          meSender,
          queuedAttachments[index].file,
          index === 0 ? trimmedComposer || undefined : undefined,
          index === 0
            ? {
                ...(quotedMessageIdForSend ? { quotedMessageId: quotedMessageIdForSend } : {}),
                ...(trimmedComposer ? buildWhatsAppPreviewOptions(trimmedComposer) : {}),
              }
            : undefined,
        );

        if (sentMessage) {
          onMessagesReconciled(conversationId, [{
            optimisticId: optimisticMessages[index].id,
            message: sentMessage,
          }]);
        } else {
          failedIndex = index;
          break;
        }
      } catch (error) {
        failedIndex = index;
        failureMessage = resolveSendErrorMessage(error, t('failedToSendAttachment'));
        break;
      }
    }

    if (failedIndex === null) {
      return;
    }

    const failedOptimisticIds = optimisticMessages.slice(failedIndex).map(message => message.id);
    onMessagesRemoved(conversationId, failedOptimisticIds);
    setAttachments(queuedAttachments.slice(failedIndex));

    if (failedIndex === 0) {
      setComposer(rollback.composer);
      setSelectedMentions(rollback.selectedMentions);
      setQuotedMessage(rollback.quotedMessage);
    }

    setSendError(failureMessage);
    } finally {
      if (isAttachmentSend) {
        setSendingMessage(false);
        setSendingProgressLabel(null);
      }
    }
  }, [
    attachments,
    clearAttachments,
    clearInteractiveComposer,
    composer,
    conversationId,
    hasInteractiveDraft,
    interactiveButtonsDraft,
    interactiveComposerMode,
    interactiveListDraft,
    isInteractiveComposerOpen,
    isInteractiveModeActive,
    meSender,
    buildReplyOptions,
    onMessagesCommitted,
    onMessagesReconciled,
    onMessagesRemoved,
    quotedMessage,
    quotedMessageIdForSend,
    instagramCommentTargetMessage,
    messengerCommentTargetMessage,
    buildWhatsAppPreviewOptions,
    selectedMentions,
    stopWebchatTyping,
    t,
    resolveSendErrorMessage,
  ]);

  const onRefreshScheduledMessages = useCallback(async () => {
    if (!conversationId) {
      setScheduledMessages([]);
      return;
    }

    try {
      const items = await getScheduledMessages(conversationId);
      setScheduledMessages(items);
    } catch (error) {
      setScheduleError(error instanceof Error ? error.message : t('failedToLoadScheduledMessages'));
    }
  }, [conversationId, t]);

  const runSchedule = useCallback(async (explicitScheduleTime?: Date) => {
    if (!conversationId || !meSender || scheduling) return;

    if (isInteractiveModeActive) {
      setScheduleError(t('interactiveCannotBeScheduled'));
      return;
    }

    const trimmedComposer = composer.trim();
    if (!trimmedComposer) {
      setScheduleError(t('typeMessageToSchedule'));
      return;
    }

    const scheduleFor = explicitScheduleTime ?? scheduledAt ?? new Date(Date.now() + 5 * 60 * 1000);

    setScheduling(true);
    setScheduleError(null);

    try {
      if (attachments.length === 0) {
        const created = await scheduleMessage(conversationId, meSender, trimmedComposer, scheduleFor, quotedMessageIdForSend);
        if (!created) {
          setScheduleError(t('failedToScheduleMessage'));
          return;
        }
      } else {
        let failedIndex: number | null = null;
        let failureMessage = t('failedToScheduleAttachment');

        for (let index = 0; index < attachments.length; index += 1) {
          try {
            const scheduledAttachment = await scheduleAttachmentMessage(
              conversationId,
              meSender,
              attachments[index].file,
              scheduleFor,
              index === 0 ? trimmedComposer || undefined : undefined,
              index === 0 ? quotedMessageIdForSend : undefined,
            );

            if (!scheduledAttachment) {
              failedIndex = index;
              failureMessage = t('failedToScheduleAttachment');
              break;
            }
          } catch (error) {
            failedIndex = index;
            failureMessage = resolveSendErrorMessage(error, t('failedToScheduleAttachment'));
            break;
          }
        }

        if (failedIndex !== null)
        {
          const remainingIds = new Set(attachments.slice(failedIndex).map(item => item.id));
          setAttachments(current => current.filter(item => remainingIds.has(item.id)));
          if (failedIndex > 0) {
            setComposer('');
            setAiDraft('');
            setAiDraftError(null);
          }
          setScheduleError(failureMessage);
          return;
        }

        clearAttachments();
      }

      setComposer('');
      setAiDraft('');
      setAiDraftError(null);
      setScheduledAt(null);
      setQuotedMessage(null);
      await onRefreshScheduledMessages();
    } catch (error) {
      setScheduleError(resolveSendErrorMessage(error, t('failedToScheduleMessage')));
    } finally {
      setScheduling(false);
    }
  }, [attachments, clearAttachments, composer, conversationId, isInteractiveModeActive, meSender, onRefreshScheduledMessages, quotedMessageIdForSend, resolveSendErrorMessage, scheduledAt, scheduling, t]);

  const onSchedule = useCallback(async () => {
    await runSchedule();
  }, [runSchedule]);

  const onScheduleAt = useCallback(async (nextScheduledAt: Date) => {
    await runSchedule(nextScheduledAt);
  }, [runSchedule]);

  const onCancelScheduledMessage = useCallback(async (scheduledMessageId: string) => {
    if (!conversationId) return;

    try {
      await cancelScheduledMessage(conversationId, scheduledMessageId);
      await onRefreshScheduledMessages();
    } catch (error) {
      setScheduleError(error instanceof Error ? error.message : t('failedToCancelScheduledMessage'));
    }
  }, [conversationId, onRefreshScheduledMessages, t]);

  const onUpdateScheduledMessageTime = useCallback(async (scheduledMessageId: string, nextScheduleTime: Date, content?: string) => {
    if (!conversationId) return;

    try {
      await updateScheduledMessageTime(conversationId, scheduledMessageId, nextScheduleTime, content);
      await onRefreshScheduledMessages();
    } catch (error) {
      setScheduleError(error instanceof Error ? error.message : t('failedToUpdateScheduledMessage'));
    }
  }, [conversationId, onRefreshScheduledMessages, t]);

  const onGenerateAiDraft = useCallback(async () => {
    if (!conversationId || !meSender || aiDraftLoading || isInteractiveModeActive) return;

    setAiDraftLoading(true);
    setAiDraftError(null);

    try {
      const draft = await generateAiDraft(conversationId, composer.trim() || undefined);
      if (!draft?.content) {
        setAiDraftError(t('aiCouldNotGenerateReplyDraft'));
        return;
      }

      setAiDraft(draft.content);
      if (composer.trim()) {
        setComposer('');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToGenerateAiDraft');
      setAiDraftError(message);
      if (onAiDraftFailure) {
        onAiDraftFailure(message);
      }
    } finally {
      setAiDraftLoading(false);
    }
  }, [aiDraftLoading, composer, conversationId, isInteractiveModeActive, meSender, onAiDraftFailure, t]);

  const onUseAiDraft = useCallback(() => {
    if (!aiDraft) return;
    setComposer(aiDraft);
    setAiDraft('');
    setAiDraftError(null);
    focusComposerAtEnd(aiDraft);
  }, [aiDraft, focusComposerAtEnd]);

  const onUseAiSummary = useCallback(() => {
    if (!aiSummary) return;
    setComposer(aiSummary);
    setAiSummary('');
    setAiSummaryError(null);
    focusComposerAtEnd(aiSummary);
  }, [aiSummary, focusComposerAtEnd]);

  const onDismissAiDraft = useCallback(() => {
    setAiDraft('');
    setAiDraftError(null);
  }, []);

  const onGenerateAiSummary = useCallback(async () => {
    if (!conversationId || !meSender || aiSummaryLoading || isInteractiveModeActive) return;

    setAiSummaryLoading(true);
    setAiSummaryError(null);

    try {
      const summary = await generateAiSummary(conversationId);
      if (!summary?.content) {
        setAiSummaryError(t('aiCouldNotGenerateSummary'));
        return;
      }

      setAiSummary(summary.content);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToGenerateAiSummary');
      setAiSummaryError(message);
    } finally {
      setAiSummaryLoading(false);
    }
  }, [aiSummaryLoading, conversationId, isInteractiveModeActive, meSender, t]);

  const onDismissAiSummary = useCallback(() => {
    setAiSummary('');
    setAiSummaryError(null);
  }, []);

  const onComposerDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!dragActive) {
      setDragActive(true);
    }
  }, [dragActive]);

  const onComposerDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setDragActive(false);
  }, []);

  const onComposerDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    setIsUploaderExpanded(true);
    onPickAttachments(event.dataTransfer.files);
  }, [onPickAttachments]);

  useEffect(() => {
    const previousAttachments = attachmentRef.current;
    const removedAttachments = previousAttachments.filter(previousAttachment => !attachments.some(attachment => attachment.id === previousAttachment.id));
    revokePendingAttachments(removedAttachments);
    attachmentRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    if (attachments.length > 0) {
      setIsUploaderExpanded(true);
    }
  }, [attachments.length]);

  useEffect(() => {
    return () => {
      stopAudioRecording();
      stopRecordingStream();
    };
  }, [stopAudioRecording, stopRecordingStream]);

  useEffect(() => {
    return () => {
      revokePendingAttachments(attachmentRef.current);
    };
  }, []);

  useEffect(() => {
    setAiDraft('');
    setAiDraftError(null);
    setAiDraftLoading(false);
    setAiSummary('');
    setAiSummaryError(null);
    setAiSummaryLoading(false);
    setSendError(null);
    setScheduleError(null);
    setScheduledAt(null);
    setScheduledMessages([]);
    setMentionSuggestions([]);
    setMentionMenuOpen(false);
    setMentionActiveIndex(0);
    setMentionTrigger(null);
    updateComposerCaretPosition(0);
    setSelectedMentions([]);
    setQuotedMessage(null);
    setWhatsAppLinkPreviewEnabled(true);
    lastWhatsAppTypingSignalAtRef.current = 0;
    lastInstagramTypingSignalAtRef.current = 0;
    lastWebchatTypingSignalAtRef.current = 0;
    setIsShortcutMenuOpen(false);
    clearAttachments();
    clearInteractiveComposer();
  }, [clearAttachments, clearInteractiveComposer, resetKey, updateComposerCaretPosition]);

  useEffect(() => {
    void onRefreshScheduledMessages();
  }, [onRefreshScheduledMessages]);

  return {
    composer,
    setComposer,
    quotedMessage,
    mentionSuggestions,
    mentionMenuOpen,
    mentionActiveIndex,
    interactiveComposerMode,
    setInteractiveComposerMode,
    interactiveListDraft,
    setInteractiveListDraft,
    interactiveButtonsDraft,
    setInteractiveButtonsDraft,
    isInteractiveComposerOpen,
    setIsInteractiveComposerOpen,
    hasInteractiveListDraft,
    hasInteractiveButtonsDraft,
    hasInteractiveDraft,
    attachments,
    isUploaderExpanded,
    setIsUploaderExpanded,
    isRecordingAudio,
    isShortcutMenuOpen,
    setIsShortcutMenuOpen,
    dragActive,
    sendError,
    sendingMessage,
    sendingProgressLabel,
    aiDraft,
    aiDraftError,
    aiDraftLoading,
    aiSummary,
    aiSummaryError,
    aiSummaryLoading,
    scheduledAt,
    setScheduledAt,
    scheduling,
    scheduleError,
    scheduledMessages,
    onSchedule,
    onScheduleAt,
    onRefreshScheduledMessages,
    onCancelScheduledMessage,
    onUpdateScheduledMessageTime,
    onSend,
    onGenerateAiDraft,
    onGenerateAiSummary,
    onAudioRecordToggle,
    onShortcutSelectCommand,
    insertComposerEmoji,
    clearAttachments,
    removeAttachment,
    onPickAttachments,
    onComposerDragOver,
    onComposerDragLeave,
    onComposerDrop,
    onComposerChange,
    onComposerSelectionChange,
    onComposerFocus,
    onComposerKeyDown,
    onSelectMention,
    onQuoteMessage,
    onSelectInstagramCommentTarget,
    onClearInstagramCommentTarget,
    onSelectMessengerCommentTarget,
    onClearMessengerCommentTarget,
    onReplyWithInteractiveSelection,
    onClearQuotedMessage,
    onUseAiDraft,
    onUseAiSummary,
    onDismissAiDraft,
    onDismissAiSummary,
    showWhatsAppLinkPreviewToggle,
    whatsAppLinkPreviewEnabled,
    setWhatsAppLinkPreviewEnabled,
    instagramComposerContext,
    instagramReplyMode,
    setInstagramReplyMode: setInstagramReplyModeOverride,
    instagramCommentTargetMessageId,
    setInstagramCommentTargetMessageId,
    messengerComposerContext,
    messengerReplyMode,
    setMessengerReplyMode: setMessengerReplyModeOverride,
    messengerCommentTargetMessageId,
    setMessengerCommentTargetMessageId,
  };
};

export default useMessageComposer;
